import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";

export type LayoutMode = "web" | "native";

export type Platform = "web" | "ios" | "android";

/**
 * In dev, the preview tool can force native mode via a query param.
 * `_devnative=ios` or `_devnative=android` (or `_devnative=1` → defaults to ios).
 */
function getDevNativeOverride(): Platform | null {
  if (
    process.env.NODE_ENV !== "development" &&
    process.env.NEXT_PUBLIC_ENVIRONMENT !== "development"
  ) {
    return null;
  }
  if (typeof window === "undefined") return null;
  const val = new URLSearchParams(window.location.search).get("_devnative");
  if (!val) return null;
  if (val === "android") return "android";
  return "ios"; // "1" or "ios" both default to ios
}

export function useLayoutMode() {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>();
  const [platform, setPlatform] = useState<Platform>("web");
  const [isDevPreview, setIsDevPreview] = useState(false);

  useEffect(() => {
    const devPlatform = getDevNativeOverride();
    if (devPlatform) {
      setIsDevPreview(true);
      setLayoutMode("native");
      setPlatform(devPlatform);
    } else if (Capacitor.isNativePlatform()) {
      setLayoutMode("native");
      if (Capacitor.getPlatform() === "ios") {
        setPlatform("ios");
      } else if (Capacitor.getPlatform() === "android") {
        setPlatform("android");
      }
    } else {
      setLayoutMode("web");
    }
  }, []);

  const isNativeIOS = platform === "ios";
  // In dev preview the iframe is much smaller than the host `screen.width`,
  // so trust the iframe viewport instead. Outside dev preview, `screen.width`
  // matches the device's physical screen (correct for Capacitor).
  const widthSource = isDevPreview
    ? typeof window !== "undefined"
      ? window.innerWidth
      : 0
    : typeof screen !== "undefined"
      ? screen.width
      : 0;
  const isIPad = isNativeIOS && widthSource >= 768;

  return { layoutMode, platform, isDevPreview, isNativeIOS, isIPad };
}
